import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EngineService } from '@/services/engine';
import { SourceFolder, SourceFolderChildren } from '@/types/source-folder';
import { cleanupAfterSourceDeletion } from '@/stores';
import { revalidateIdentityViews } from '@/utils/swr';
import {
    revalidateAlertUnitCapacity,
    revalidateOrganizationConfiguration,
} from '@/features/SettingsView/hooks/useConfiguration';
import { revalidateSourceFolders } from '@/hooks';
import { toast } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';

interface Options {
    selectedFolderId: string | null;
    clearFolderSelection: () => void;
    folderChildren: SourceFolderChildren | undefined;
}

export function useFolderOperations({
    selectedFolderId,
    clearFolderSelection,
    folderChildren,
}: Options) {
    const { t } = useTranslation(I18nNamespace.SOURCES);

    const [folderToEdit, setFolderToEdit] = useState<SourceFolder | null>(null);
    const [folderToDelete, setFolderToDelete] = useState<SourceFolder | null>(
        null,
    );
    const [isDeletingFolder, setIsDeletingFolder] = useState(false);

    const sourceIdsInFolderToDelete =
        folderToDelete &&
        selectedFolderId === folderToDelete.id &&
        folderChildren?.sources
            ? folderChildren.sources.map((s) => s.id)
            : [];

    const folderDeleteSourceCount = folderToDelete
        ? Math.max(
              folderToDelete.sourceCount ?? 0,
              sourceIdsInFolderToDelete.length,
          )
        : 0;

    const handleEditFolder = (folder: SourceFolder) => setFolderToEdit(folder);
    const handleDeleteFolder = (folder: SourceFolder) =>
        setFolderToDelete(folder);

    const handleDeleteFolderConfirm = useCallback(
        async (deleteSourcesToo: boolean) => {
            if (!folderToDelete || isDeletingFolder) return;
            setIsDeletingFolder(true);
            try {
                if (deleteSourcesToo) {
                    const sourceIdsToRemove =
                        sourceIdsInFolderToDelete.length > 0
                            ? sourceIdsInFolderToDelete
                            : (
                                  await EngineService.getSourceFolderChildren(
                                      folderToDelete.id,
                                  )
                              ).sources.map((s) => s.id);
                    for (const sourceId of sourceIdsToRemove) {
                        await EngineService.deleteSource(sourceId);
                        cleanupAfterSourceDeletion(sourceId);
                    }
                    await revalidateIdentityViews();
                    await revalidateOrganizationConfiguration();
                    await revalidateAlertUnitCapacity();
                }
                await EngineService.deleteSourceFolder(folderToDelete.id);
                setFolderToDelete(null);
                clearFolderSelection();
                await revalidateSourceFolders();
                toast.success(t('folders.deleteFolderSuccess'));
            } catch {
                toast.error(t('folders.deleteFolderError'));
            } finally {
                setIsDeletingFolder(false);
            }
        },
        [
            folderToDelete,
            isDeletingFolder,
            sourceIdsInFolderToDelete,
            clearFolderSelection,
            t,
        ],
    );

    return {
        folderToEdit,
        setFolderToEdit,
        folderToDelete,
        setFolderToDelete,
        isDeletingFolder,
        sourceIdsInFolderToDelete,
        folderDeleteSourceCount,
        handleEditFolder,
        handleDeleteFolder,
        handleDeleteFolderConfirm,
    };
}
